import 'react-datepicker/dist/react-datepicker.min.css';
import classNames from 'classnames';
import * as React from 'react';
import type { ReactDatePickerCustomHeaderProps, DatePickerProps as ReactDatePickerProps } from 'react-datepicker';
import ReactDatePicker from 'react-datepicker';
import { useTheme } from '~/Features/Layouts/Theme/Context/ThemeContextProvider';
import type { StrictOmit } from '~/Partials/Utils/typeUtils';
import type { Theme } from '~/Utils/Theme';
import { TimeFrameSwitcher } from './Components/TimeFrameSwitcher/TimeFrameSwitcher';
import styles from './DatePicker.module.css';
import type { ConfigProps } from './Utils/config';
import { getPropsFromConfig } from './Utils/config';
import type { DatePickerHeaderProps } from './Utils/types';

type ExposedReactDatePickerProps = Pick<
    ReactDatePickerProps,
    'open' | 'openToDate' | 'filterDate' | 'onMonthChange' | 'children' | 'className'
>;

interface DatePickerBaseProps extends ExposedReactDatePickerProps {
    selectedDay: Date | null;
    onChange: (date: Date | null) => void;
    config?: StrictOmit<ConfigProps, 'selectedDay'>;
    theme?: Pick<Theme, 'withRoundedCorners'>;
    formatWeekNumber?: (date: Date) => string | number;
    ariaHeader?: {
        id: string;
        label: string;
        headerType: keyof Pick<React.ReactHTML, 'h1' | 'h2' | 'h3' | 'h4'>;
    };
}

export type DatePickerProps = DatePickerBaseProps & DatePickerHeaderProps<ReactDatePickerCustomHeaderProps>;

export const DatePicker = React.forwardRef<ReactDatePicker, DatePickerProps>((props, calendarRef) => {
    const contextualTheme = useTheme();

    const {
        config,
        selectedDay,
        renderCustomHeader,
        headerProps,
        children,
        ariaHeader,
        className,
        theme = contextualTheme,
        formatWeekNumber,
        ...restOfProps
    } = props;

    const { withRoundedCorners } = theme;

    return (
        <div className={classNames(withRoundedCorners && styles['Variant--rounded'], className)}>
            {ariaHeader &&
                React.createElement(
                    ariaHeader.headerType,
                    { id: ariaHeader.id, className: 'visuallyHidden' },
                    ariaHeader.label
                )}
            <ReactDatePicker
                {...getPropsFromConfig({
                    ...config,
                    selectedDay,
                })}
                {...restOfProps}
                // Small hack as the library claims that it only supports number as a return type while it works pretty
                // well with string as well. We do have unit test for this just in case it changes in the future.
                formatWeekNumber={formatWeekNumber as ((date: Date) => number) | undefined}
                ref={calendarRef}
                renderCustomHeader={customHeaderProps =>
                    renderCustomHeader ? (
                        renderCustomHeader(customHeaderProps)
                    ) : (
                        <TimeFrameSwitcher<ReactDatePickerCustomHeaderProps>
                            customHeaderProps={customHeaderProps}
                            {...headerProps}
                        />
                    )
                }
            >
                {children}
            </ReactDatePicker>
        </div>
    );
});
